// checklistBreadcrumb — resolves the "location inside the book" path shown
// at the top of the checklist detail popup.
//
// Segment sources (in priority order):
//   1. Callers pass an explicit `breadcrumb` when opening the popup — this
//      wins so whichever surface hosts the checklist (manage panel, board
//      view, checklist canvas, calendar, etc.) can put its own current
//      location as the parent, matching the user's mental model of where
//      they were when they opened the popup.
//   2. Fallback derivation from the checklist item: walk the
//      `parentContainerId` up the CanvasElement tree, prefixed with the
//      book label (Individual / Daily / Weekly). Recurring items with a
//      synthetic `recurring:{workspaceId}:{mode}` parent get only the book
//      + focused date.
//
// The segment list is deliberately open-ended — some checklists are
// attached at board or goal level, some go down to subtask; render however
// many segments actually resolve.

import type { ChecklistItem } from '../types/schema'
import type { TimelineElement } from '../types/timelineElement'
import { parseRecurringContainerId } from '../context/recurringLensContextCore'

export type ChecklistBreadcrumbKind = 'BOOK' | 'BOARD' | 'GOAL' | 'TASK' | 'SUBTASK' | 'CONTEXT'

export type ChecklistBreadcrumbSegment = {
  kind: ChecklistBreadcrumbKind
  label: string
  /** Optional href / target id for future navigation hooks. Purely
   *  informational today — rendering ignores it. */
  targetId?: string
}

const BOOK_LABEL: Record<'INDIVIDUAL' | 'DAILY' | 'WEEKLY', string> = {
  INDIVIDUAL: 'Individual book',
  DAILY: 'Daily book',
  WEEKLY: 'Weekly book',
}

const ELEMENT_KIND_TO_BREADCRUMB: Record<'GOAL' | 'TASK' | 'SUBTASK', ChecklistBreadcrumbKind> = {
  GOAL: 'GOAL',
  TASK: 'TASK',
  SUBTASK: 'SUBTASK',
}

/**
 * Resolve default breadcrumb segments for a checklist item.
 *
 * The book segment is always first. Recurring items only get the book +
 * optional focused date, since their parent container is synthetic.
 * Individual items climb the CanvasElement tree via `parentId`.
 */
export const resolveDefaultBreadcrumb = (
  item: Pick<ChecklistItem, 'checklistType' | 'parentContainerId' | 'workspaceId'> | null | undefined,
  elements: TimelineElement[],
  options: {
    focusedOccurrenceLabel?: string | null
  } = {},
): ChecklistBreadcrumbSegment[] => {
  if (!item) return []
  const type = item.checklistType ?? 'INDIVIDUAL'
  const bookKey: 'INDIVIDUAL' | 'DAILY' | 'WEEKLY' =
    type === 'DAILY' || type === 'WEEKLY' ? type : 'INDIVIDUAL'
  const segments: ChecklistBreadcrumbSegment[] = [
    { kind: 'BOOK', label: BOOK_LABEL[bookKey] },
  ]

  const recurring = parseRecurringContainerId(item.parentContainerId ?? '')
  if (recurring) {
    if (options.focusedOccurrenceLabel) {
      segments.push({ kind: 'CONTEXT', label: options.focusedOccurrenceLabel })
    }
    return segments
  }

  // Walk parent chain via TimelineElement.parentId. Bail after a small
  // depth so a corrupt cycle can't wedge the header.
  const byId = new Map(elements.map((element) => [element.id, element]))
  const chain: TimelineElement[] = []
  let currentId: string | null = item.parentContainerId ?? null
  let hops = 0
  while (currentId && hops < 8) {
    const element = byId.get(currentId)
    if (!element) break
    chain.push(element)
    currentId = element.parentId ?? null
    hops += 1
  }
  chain.reverse().forEach((element) => {
    segments.push({
      kind: ELEMENT_KIND_TO_BREADCRUMB[element.type] ?? 'CONTEXT',
      label: element.title || `Untitled ${element.type.toLowerCase()}`,
      targetId: element.id,
    })
  })
  return segments
}
